/**
 * Quran scope utilities: convert (surah/hizb/full) into ordered ayah arrays.
 *
 * Ayahs are fetched lazily from the backend (api/quran). Hizb ranges use
 * the canonical /api/quran/hizb-index map. We keep an in-memory cache.
 */
import { quranApi } from '../services/api';
import api from '../services/api';

let _hizbIndexCache = null; // [{hizb_number, start_global_ayah, end_global_ayah, ...}]
let _hizbIndexPromise = null;

/** @returns {Promise<Array>} */
export async function getHizbIndex() {
  if (_hizbIndexCache) return _hizbIndexCache;
  if (_hizbIndexPromise) return _hizbIndexPromise;
  _hizbIndexPromise = api.get('/quran/hizb-index').then((r) => {
    _hizbIndexCache = r.data;
    return _hizbIndexCache;
  });
  return _hizbIndexPromise;
}

/** Return all ayahs of a surah, normalized to the audio module shape. */
export async function getAyahsForSurah(surahNumber) {
  const r = await quranApi.getVerses(surahNumber);
  return r.data
    .filter((v) => v.global_ayah_number)
    .map((v) => ({
      surahNumber: v.surah_number,
      ayahNumber: v.verse_number,
      ayahKey: v.verse_key,
      globalAyahNumber: v.global_ayah_number,
      hizbNumber: v.hizb_number,
      textAr: v.arabic_text,
      textFr: v.translation_french,
    }));
}

/** Return the start/end of a hizb (1..60). */
export async function getAyahsForHizb(hizbNumber) {
  const idx = await getHizbIndex();
  const entry = idx.find((h) => h.hizb_number === Number(hizbNumber));
  if (!entry) return [];
  // Resolve all ayahs in the global range. We fan-out by surah to leverage
  // the cached /api/quran/surahs/{n}/verses endpoint when possible.
  const out = [];
  // Fetch surahs list once to know boundaries
  const allSurahs = await quranApi.getSurahs().then((r) => r.data);
  // We iterate ayahs by surah, collecting only those whose hizb matches.
  for (const s of allSurahs) {
    // Quick skip: if the surah's last quran_order < entry.start_global, skip.
    // We don't have surah quran_order bounds locally; fetch verses lazily.
    const verses = await getAyahsForSurah(s.number);
    for (const v of verses) {
      if (v.hizbNumber === Number(hizbNumber)) {
        out.push(v);
      }
    }
    // Optimization: stop once we've passed the end of the hizb.
    if (verses.length && verses[verses.length - 1].globalAyahNumber > entry.end_global_ayah) {
      break;
    }
  }
  return out;
}

/** Return ayahs for the full Quran in canonical order. */
export async function getAyahsForFullQuran() {
  const surahs = await quranApi.getSurahs().then((r) => r.data);
  const all = [];
  for (const s of surahs) {
    const verses = await getAyahsForSurah(s.number);
    all.push(...verses);
  }
  return all;
}

/**
 * Given an ayah, return the canonical "scope" that contains it
 * (used to suggest a download when only one ayah is requested).
 */
export function getScopeForAyah(ayah) {
  if (!ayah) return null;
  return {
    type: 'surah',
    surahNumber: ayah.surahNumber,
    hizbNumber: ayah.hizbNumber,
  };
}

/**
 * Given a memorization scope (range of ayahs), choose the best download unit.
 * Rules:
 *  - if entirely within one surah AND the surah is small (<=80 ayahs) -> 'surah'
 *  - else if entirely within one hizb -> 'hizb'
 *  - else -> 'surah' (still preferred over Quran-wide for small ranges)
 *  - never proposes verset / never proposes juz
 */
export async function getDownloadOptionsForMemorizationScope(scope) {
  if (!scope) return [];
  const ayahsInSurah = await getAyahsForSurah(scope.surahNumber);
  const target = ayahsInSurah.filter((v) => {
    if (scope.type === 'full_surah') return true;
    return v.ayahNumber >= (scope.fromAyah || 1)
      && v.ayahNumber <= (scope.toAyah || ayahsInSurah.length);
  });
  if (!target.length) return [];
  const hizbs = new Set(target.map((a) => a.hizbNumber).filter(Boolean));
  const options = [{ scopeType: 'surah', surahNumber: scope.surahNumber }];
  if (hizbs.size === 1) {
    options.unshift({ scopeType: 'hizb', hizbNumber: [...hizbs][0] });
  }
  return options;
}
